import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { BoundarySide } from "../api/types";
import { Icon } from "./icons";
import { toast } from "./toast";

const TAIL_SECONDS = 12; // how much of the take's end to audition for "Play end"

/**
 * One "Audio start" / "Audio end" boundary card, split into two panels:
 *  - LEFT: a manuscript simulation — a few lines of context with the fragment
 *    the audio actually covers shaded green (so you can see exactly where the
 *    narration begins / ends in the text).
 *  - RIGHT: audition the take's head/tail, open the PDF at the page, or
 *    confirm it looks right — beside an always-open "Starts/Ends on page" box
 *    to correct the mapping.
 * Reused in the report and in the project-row popover. Pages are physical
 * (match the manuscript pane's "p. N" and the PDF's #page=N). A correction
 * saves the new page instantly (no wait); the parent debounces a single
 * background re-proof, signalled via onSaved.
 */
export function BoundaryCard({
  projectId,
  chapterId,
  which,
  side,
  pdfUrl,
  offset = 0,
  onSaved,
  onConfirmed,
  compact,
  collapsible,
}: {
  projectId: string;
  chapterId: string;
  which: "start" | "end";
  side: BoundarySide;
  pdfUrl: string;
  offset?: number; // printed − physical; card pages are PRINTED, the PDF link is physical
  onSaved?: () => void; // a page was corrected — parent schedules the re-proof
  onConfirmed?: () => void;
  compact?: boolean;
  collapsible?: boolean;
}) {
  const qc = useQueryClient();
  // Collapsible cards (the report's pinned Start/End) start collapsed so they
  // frame the findings instead of burying them; click the header to review.
  const [expanded, setExpanded] = useState(!collapsible);
  const page = side.current_page ?? side.detected_page ?? null;
  const [pageInput, setPageInput] = useState<string>(page != null ? String(page) : "");
  // Follow the saved page (a correction here, or a refetch) back into the box.
  useEffect(() => { setPageInput(page != null ? String(page) : ""); }, [page]);
  const unchanged = page != null && pageInput.trim() === String(page);
  const inputId = useId();
  const title = which === "start" ? "Audio start" : "Audio end";

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const durRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const audioUrl = `/api/projects/${projectId}/chapters/${chapterId}/audio`;

  // Stop playback when the card unmounts (popover closes / report switches).
  useEffect(() => () => { try { audioRef.current?.pause(); } catch { /* ignore */ } }, []);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["project", projectId] });
    qc.invalidateQueries({ queryKey: ["result", projectId] });
    // The project-page popover reads this card's side from ["boundary", chapterId]
    // (the report reads it from ["result"]). Without this, confirming/correcting
    // from the project page left the card stale — "Looks right ✓" never cleared.
    qc.invalidateQueries({ queryKey: ["boundary", chapterId] });
  }

  const act = useMutation({
    mutationFn: (vars: { action: "confirm" | "correct"; page?: number }) =>
      api<{ job_id: string | null }>(
        `/api/modules/proofing/projects/${projectId}/chapters/${chapterId}/boundary`,
        { method: "POST", body: JSON.stringify({ which, action: vars.action, page: vars.page }) }
      ),
    onSuccess: (_r, vars) => {
      refresh();
      if (vars.action === "confirm") {
        toast(`${title} confirmed.`, "success");
        onConfirmed?.();
      } else {
        toast(`${title} set to p. ${vars.page} — re-proofing shortly.`, "success");
        onSaved?.();
      }
    },
    onError: (e: any) => toast(e.message || "Couldn't update the boundary.", "error"),
  });

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) { a.pause(); return; }

    // Where to start: the head (0) for "start", the last TAIL_SECONDS for "end".
    const target = () => {
      if (which === "start") return 0;
      const d = durRef.current || a.duration;
      return isFinite(d) && d > 0 ? Math.max(0, d - TAIL_SECONDS) : 0;
    };
    // Surface a real failure instead of swallowing it — a silent catch here is
    // why "Play start" looked dead (e.g. a transient load error on the R2-backed,
    // redirect-served audio leaves the element unable to play).
    const startPlaying = () => {
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch((err: any) =>
          toast(`Couldn't play the audio (${err?.name || "error"}). Try reopening the report.`, "error"));
      }
    };

    // If the element never loaded or errored during preload, force a fresh fetch
    // and play once it's ready (the bare <audio> doesn't retry on its own).
    if (a.error || a.readyState === 0) {
      a.addEventListener("loadedmetadata", () => { try { a.currentTime = target(); } catch { /* ignore */ } startPlaying(); }, { once: true });
      a.load();
      return;
    }

    // Seek (if needed) then resume on `seeked` AND immediately — mirroring the
    // main player, so a not-yet-seekable element still plays once it settles.
    a.pause();
    const t = target();
    if (Math.abs(a.currentTime - t) > 0.25) {
      try { a.currentTime = t; } catch { /* not seekable yet */ }
      a.addEventListener("seeked", startPlaying, { once: true });
    }
    startPlaying();
  }

  function openPdf() {
    // `page` is the PRINTED page; the PDF viewer indexes by physical page.
    if (page != null) window.open(`${pdfUrl}#page=${Math.max(1, page - offset)}`, "_blank", "noopener");
  }
  function saveCorrection() {
    const n = Number(pageInput);
    if (!Number.isFinite(n) || n < 1) {
      toast("Enter a valid page number.", "error");
      return;
    }
    act.mutate({ action: "correct", page: Math.round(n) });
  }

  const score = which === "start" ? side.confidence : side.tail_score;
  const lowConfidence = !side.confirmed && score != null && score < 0.55;

  return (
    <div className={`boundary-card${compact ? " compact" : ""}${side.confirmed ? " confirmed" : ""}${collapsible ? " collapsible" : ""}`}>
      <div
        className="bc-head"
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
        onKeyDown={collapsible ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded((v) => !v); } } : undefined}
      >
        <strong>
          {collapsible && <span className={`bc-caret${expanded ? " open" : ""}`}>▸</span>}
          {title}
        </strong>
        <span className="muted">
          {side.confirmed ? (
            <><Icon name="check" size={14} /> Confirmed</>
          ) : lowConfidence ? (
            "Unverified — please check"
          ) : (
            "Detected"
          )}
          {page != null && <> · p. {page}</>}
        </span>
      </div>

      {expanded && (
      <div className="bc-panels">
        {/* LEFT — manuscript simulation; green = covered by the audio. */}
        <div className="bc-ms">
          <div className="bc-ms-label">
            {which === "start" ? "Manuscript — audio begins at the green" : "Manuscript — audio ends at the green"}
          </div>
          {which === "start" ? (
            <>
              <p>
                {side.snippet_before && <span className="bc-dim">…{side.snippet_before} </span>}
                <span className="bc-covered">{side.snippet_at || "—"}…</span>
              </p>
              {!side.snippet_before && (
                <p className="bc-note">Nothing precedes this in the manuscript — the audio begins at the very start.</p>
              )}
            </>
          ) : (
            <>
              <p>
                <span className="bc-covered">…{side.snippet_at || "—"}</span>
                {side.snippet_after && <span className="bc-dim"> {side.snippet_after}…</span>}
              </p>
              {!side.snippet_after && (
                <p className="bc-note">Nothing follows in the manuscript — the audio reaches the very end. (If the chapter should continue past here, the audio may have stopped short.)</p>
              )}
            </>
          )}
        </div>

        {/* RIGHT — two columns: audition + verify, and the page fix, which is
            always open so a correction never needs an extra click. */}
        <div className="bc-controls">
          <div className="bc-actions">
            <button className="btn sm" onClick={togglePlay}>
              <Icon name={playing ? "pause" : "play"} size={14} /> {which === "start" ? "Play start" : "Play end"}
            </button>
            <button className="btn ghost sm" onClick={openPdf} disabled={page == null}>View pages ↗</button>
            {!side.confirmed && (
              <button className="btn sm" disabled={act.isPending} onClick={() => act.mutate({ action: "confirm" })}>
                Looks right ✓
              </button>
            )}
          </div>
          <div className="bc-correct">
            <label className="muted" htmlFor={inputId} style={{ fontSize: 12 }}>{which === "start" ? "Starts on page" : "Ends on page"}</label>
            <input
              id={inputId}
              className="input"
              type="number"
              min={1}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !unchanged) saveCorrection(); }}
            />
            <button className="btn sm" disabled={act.isPending || unchanged} onClick={saveCorrection}>Save</button>
          </div>
        </div>
      </div>
      )}

      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onLoadedMetadata={(e) => { durRef.current = e.currentTarget.duration; }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
}
